import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Truck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

// Partner-side carrier selection for ONE shipment. The default carrier is
// pre-selected; the partner can change it per shipment and sees the REAL rate
// (base + margin) that will be billed to the client BEFORE confirming.
export function CarrierSelectPanel({ declId }) {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  async function load() {
    try {
      const res = await api(`/declarations/${declId}/shipping/carrier-options`);
      setData(res);
      setConfirmed(res.chosen || null);
      const opts = res.options || [];
      const pre = res.chosen?.method_id
        ?? opts.find((o) => o.carrier === res.selected_carrier)?.method_id
        ?? opts[0]?.method_id;
      setSelectedId(pre != null ? String(pre) : "");
    } catch (e) { toast.error(e.message || "Chargement des transporteurs impossible"); }
  }
  useEffect(() => { if (declId) load(); /* eslint-disable-next-line */ }, [declId]);

  if (!data) return (
    <div className="py-6 grid place-items-center text-slate-400" data-testid="carrier-panel-loading"><FindoSpinner size={22} /></div>
  );

  const options = data.options || [];
  const selected = options.find((o) => String(o.method_id) === String(selectedId));
  const billed = selected?.client_price_eur;
  const locked = !!data.already_paid;

  async function confirm() {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await api(`/declarations/${declId}/shipping/select-carrier`, {
        method: "POST", body: { method_id: selected.method_id, carrier: selected.carrier },
      });
      setConfirmed(res.quote);
      toast.success(t("hc.carrierConfirmedCharged").replace("{price}", res.quote.client_price_eur.toFixed(2)));
    } catch (e) { toast.error(e.message || "Confirmation impossible"); }
    finally { setSaving(false); }
  }

  return (
    <div className="rounded-xl border border-border p-4 bg-white" data-testid="carrier-select-panel">
      <div className="flex items-center gap-2 text-findo-navy font-semibold text-sm">
        <Truck size={16} className="text-findo-indigo" /> {t("hc.carrierSelection")}
      </div>
      {locked ? (
        <p className="mt-2 text-sm text-slate-500" data-testid="carrier-panel-locked">
          {t("hc.shippingFeesHaveAlreadyBeen")}
        </p>
      ) : (
        <>
          <p className="mt-1 text-xs text-slate-400">
            {t("hc.defaultCarrierPreselected").replace("{m}", data.margin_pct ?? 15)}
            {data.simulated && <span className="ml-1 italic">{t("hc.simulatedRatesInPreProduction")}</span>}
          </p>
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            disabled={saving}
            className="mt-3 h-11 w-full rounded-xl border border-border px-3 text-sm"
            data-testid="carrier-select">
            {options.map((o) => (
              <option key={o.method_id} value={String(o.method_id)}>
                {o.name} ({o.carrier}) · {Number(o.client_price_eur).toFixed(2)} €
                {o.lead_time_days ? ` · ~${o.lead_time_days} j` : ""}
              </option>
            ))}
          </select>

          {billed != null && (
            <p className="mt-3 text-sm text-findo-navy" data-testid="carrier-billed-price">
              {t("hc.rateChargedToTheCustomer")} <b className="text-base">{Number(billed).toFixed(2)} €</b>
            </p>
          )}

          <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="carrier-warning">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800">{t("hc.thisRateWillBeCharged")}</p>
          </div>

          <Button onClick={confirm} disabled={saving || !selected}
            className="mt-3 w-full h-11 rounded-xl font-semibold bg-findo-indigo hover:bg-findo-indigo/90"
            data-testid="carrier-confirm-btn">
            {saving ? <><FindoSpinner size={16} className="mr-1.5" /> Confirmation…</> : t("hc.confirmTheCarrier")}
          </Button>
        </>
      )}

      {confirmed && (
        <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700" data-testid="carrier-confirmed">
          <CheckCircle2 size={15} /> {t("hc.selectedCarrier")} <b>{confirmed.method_name || confirmed.carrier}</b> — {Number(confirmed.client_price_eur).toFixed(2)} € {t("hc.chargedToCustomer")}
        </p>
      )}
    </div>
  );
}
